import React, { useMemo } from 'react';
import { calculateFinancials } from '../../services/financialCalculations';
import { formatCurrency, formatPercentage } from '../../utils/formatters';

const BessTariffArbitrageSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, arbitrageSpreadRange } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseArbitrageSpread = inputs.bessArbitrageSpread;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const arbitrageSpreadRange = [-0.5, -0.25, 0, 0.25, 0.5].map(mod => baseArbitrageSpread * (1 + mod));

        // Calculate solar-only NPV for comparison
        const solarOnlyInputs = { ...inputs, bessEnabled: false };
        const solarOnlyNPV = calculateFinancials(solarOnlyInputs).indicators.projectNPV;

        const sensitivityData = arbitrageSpreadRange.map(spread => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, bessArbitrageSpread: spread };
                const results = calculateFinancials(tempInputs);
                const bessNPV = results.indicators.projectNPV;
                const enhancement = bessNPV - solarOnlyNPV;
                return enhancement;
            });
        });

        return { sensitivityData, tariffRange, arbitrageSpreadRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Grid Tariff vs Arbitrage Spread (BESS Revenue Enhancement vs Solar-only)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Arbitrage Spread ↓</span>
                                    <span className="font-normal text-xs">Grid Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {formatPercentage(arbitrageSpreadRange[rowIndex])}
                                </td>
                                {row.map((enhancement, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${enhancement > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {formatCurrency(enhancement, 0)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BessTariffArbitrageSensitivityTable; 